"use client";

const UI_CARD_DEBUG_STORAGE_KEY = "clientReportDebugUiCardSnapshots";
const UI_CARD_DEBUG_TRAIL_KEY = "clientReportDebugUiCardTrail";
const UI_CARD_DEBUG_TRAIL_MAX = 12;
const UI_CARD_DEBUG_CARD_MAX = 20;
const RECT_TOLERANCE_PX = 1;

type UiCardDebugLayerName = "stage" | "material" | "glass" | "content" | "card";

export type ReportDebugUiCardLayerSnapshot = {
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
    top: number;
    right: number;
    bottom: number;
    left: number;
  } | null;
  computed: {
    display: string | null;
    position: string | null;
    inset: string | null;
    top: string | null;
    right: string | null;
    bottom: string | null;
    left: string | null;
    borderRadius: string | null;
    borderWidth: string | null;
    backgroundColor: string | null;
    backgroundImage: string | null;
    opacity: string | null;
    filter: string | null;
    backdropFilter: string | null;
    webkitBackdropFilter: string | null;
    maskImage: string | null;
    webkitMaskImage: string | null;
    transform: string | null;
    zIndex: string | null;
    overflow: string | null;
    boxShadow: string | null;
    pointerEvents: string | null;
  } | null;
};

export type ReportDebugUiCardSnapshot = {
  cardId: string;
  label: string | null;
  tone: string | null;
  materialModel: {
    kind: string | null;
    activeSeriesCount: number | null;
    dataPointCount: number | null;
    trend: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    ctaColor: string | null;
  };
  reason: "mount" | "after-layout" | "delayed-layout" | "report-debug";
  snapshotAtIso: string;
  routePath: string | null;
  env: {
    viewport: string | null;
    devicePixelRatio: number | null;
    platformAttribute: string | null;
    colorScheme: string | null;
  };
  cssVars: {
    iosRefractiveCardRadius: string | null;
  };
  layers: Record<UiCardDebugLayerName, ReportDebugUiCardLayerSnapshot | null>;
  diagnosis: {
    iosSelectorActive: boolean;
    glassMatchesStage: boolean | null;
    materialBleedsOutsideGlass: boolean | null;
    contentMatchesGlass: boolean | null;
    innerCardStillPaintsSurface: boolean | null;
    missingBackdropFilter: boolean | null;
    materialHardSilhouetteRisk: boolean | null;
    nestedSurfaceRisk: boolean;
  };
};

type RegisteredUiCard = {
  cardId: string;
  label?: string;
  tone?: string;
  root: HTMLElement;
};

const registeredCards = new Map<string, RegisteredUiCard>();

function readPersistedSessionJson<T>(key: string): T | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writePersistedSessionJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Debug-only persistence must never block the app.
  }
}

function roundRectValue(value: number): number {
  return Math.round(value * 100) / 100;
}

function snapshotRect(element: Element | null) {
  if (!element || typeof element.getBoundingClientRect !== "function") return null;
  const rect = element.getBoundingClientRect();
  return {
    x: roundRectValue(rect.x),
    y: roundRectValue(rect.y),
    width: roundRectValue(rect.width),
    height: roundRectValue(rect.height),
    top: roundRectValue(rect.top),
    right: roundRectValue(rect.right),
    bottom: roundRectValue(rect.bottom),
    left: roundRectValue(rect.left),
  };
}

function readComputedStyle(element: Element | null): ReportDebugUiCardLayerSnapshot["computed"] {
  if (!element || typeof window === "undefined" || typeof window.getComputedStyle !== "function") return null;
  const style = window.getComputedStyle(element);
  return {
    display: style.display || null,
    position: style.position || null,
    inset: style.inset || null,
    top: style.top || null,
    right: style.right || null,
    bottom: style.bottom || null,
    left: style.left || null,
    borderRadius: style.borderRadius || null,
    borderWidth: style.borderWidth || null,
    backgroundColor: style.backgroundColor || null,
    backgroundImage: style.backgroundImage || null,
    opacity: style.opacity || null,
    filter: style.filter || null,
    backdropFilter: style.backdropFilter || null,
    webkitBackdropFilter: style.getPropertyValue("-webkit-backdrop-filter") || null,
    maskImage: style.maskImage || null,
    webkitMaskImage: style.getPropertyValue("-webkit-mask-image") || null,
    transform: style.transform || null,
    zIndex: style.zIndex || null,
    overflow: style.overflow || null,
    boxShadow: style.boxShadow || null,
    pointerEvents: style.pointerEvents || null,
  };
}

function readCssProperty(element: Element | null, propertyName: string): string | null {
  if (!element || typeof window === "undefined" || typeof window.getComputedStyle !== "function") return null;
  return window.getComputedStyle(element).getPropertyValue(propertyName) || null;
}

function snapshotLayer(element: Element | null): ReportDebugUiCardLayerSnapshot | null {
  if (!element) return null;
  return {
    rect: snapshotRect(element),
    computed: readComputedStyle(element),
  };
}

function readDatasetNumber(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function readMaterialModel(root: HTMLElement): ReportDebugUiCardSnapshot["materialModel"] {
  return {
    kind: root.dataset.materialKind ?? null,
    activeSeriesCount: readDatasetNumber(root.dataset.materialActiveSeries),
    dataPointCount: readDatasetNumber(root.dataset.materialDataPoints),
    trend: root.dataset.materialTrend ?? null,
    primaryColor: root.dataset.materialPrimaryColor ?? null,
    secondaryColor: root.dataset.materialSecondaryColor ?? null,
    ctaColor: root.dataset.materialCtaColor ?? null,
  };
}

function rectsMatch(
  a: ReportDebugUiCardLayerSnapshot["rect"] | undefined,
  b: ReportDebugUiCardLayerSnapshot["rect"] | undefined
): boolean | null {
  if (!a || !b) return null;
  return (
    Math.abs(a.left - b.left) <= RECT_TOLERANCE_PX &&
    Math.abs(a.top - b.top) <= RECT_TOLERANCE_PX &&
    Math.abs(a.width - b.width) <= RECT_TOLERANCE_PX &&
    Math.abs(a.height - b.height) <= RECT_TOLERANCE_PX
  );
}

function rectBleedsOutside(
  outer: ReportDebugUiCardLayerSnapshot["rect"] | undefined,
  inner: ReportDebugUiCardLayerSnapshot["rect"] | undefined
): boolean | null {
  if (!outer || !inner) return null;
  return (
    outer.left < inner.left - RECT_TOLERANCE_PX ||
    outer.top < inner.top - RECT_TOLERANCE_PX ||
    outer.right > inner.right + RECT_TOLERANCE_PX ||
    outer.bottom > inner.bottom + RECT_TOLERANCE_PX
  );
}

function styleHasPaintedBackground(value: string | null | undefined): boolean {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== "transparent" && normalized !== "rgba(0, 0, 0, 0)" && normalized !== "none";
}

function styleHasVisibleBorder(value: string | null | undefined): boolean {
  if (!value) return false;
  return !/^0(px)?( 0(px)?){0,3}$/i.test(value.trim());
}

function styleHasEffect(value: string | null | undefined): boolean {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== "none" && normalized !== "0px 0px 0px rgba(0, 0, 0, 0)";
}

function styleHasMask(value: string | null | undefined): boolean {
  return styleHasEffect(value);
}

function summarizeDiagnosis(layers: ReportDebugUiCardSnapshot["layers"]) {
  const glassMatchesStage = rectsMatch(layers.glass?.rect, layers.stage?.rect);
  const materialBleedsOutsideGlass = rectBleedsOutside(layers.material?.rect, layers.glass?.rect);
  const contentMatchesGlass = rectsMatch(layers.content?.rect, layers.glass?.rect);
  const cardComputed = layers.card?.computed;
  const glassComputed = layers.glass?.computed;
  const materialComputed = layers.material?.computed;
  const iosSelectorActive = layers.glass?.computed?.display === "block" && layers.material?.computed?.display === "block";
  const innerCardStillPaintsSurface = cardComputed
    ? styleHasPaintedBackground(cardComputed.backgroundColor) ||
      styleHasPaintedBackground(cardComputed.backgroundImage) ||
      styleHasVisibleBorder(cardComputed.borderWidth) ||
      styleHasEffect(cardComputed.boxShadow) ||
      styleHasEffect(cardComputed.backdropFilter) ||
      styleHasEffect(cardComputed.webkitBackdropFilter)
    : null;
  const missingBackdropFilter = glassComputed
    ? !styleHasEffect(glassComputed.backdropFilter) && !styleHasEffect(glassComputed.webkitBackdropFilter)
    : null;
  const materialHardSilhouetteRisk = materialComputed
    ? styleHasPaintedBackground(materialComputed.backgroundImage) &&
      !styleHasEffect(materialComputed.filter) &&
      !styleHasMask(materialComputed.maskImage) &&
      !styleHasMask(materialComputed.webkitMaskImage)
    : null;
  const nestedSurfaceRisk = Boolean(
    innerCardStillPaintsSurface ||
      glassMatchesStage === false ||
      contentMatchesGlass === false ||
      materialBleedsOutsideGlass === false ||
      missingBackdropFilter ||
      materialHardSilhouetteRisk
  );

  return {
    iosSelectorActive,
    glassMatchesStage,
    materialBleedsOutsideGlass,
    contentMatchesGlass,
    innerCardStillPaintsSurface,
    missingBackdropFilter,
    materialHardSilhouetteRisk,
    nestedSurfaceRisk,
  };
}

function buildSnapshot(card: RegisteredUiCard, reason: ReportDebugUiCardSnapshot["reason"]): ReportDebugUiCardSnapshot {
  const root = card.root;
  const material = root.querySelector(".ios-refractive-card-material");
  const glass = root.querySelector(".ios-refractive-card-glass");
  const content = root.querySelector(".ios-refractive-card-content");
  const modernCard = root.querySelector(".ios-refractive-card-content > .modern-surface-card");
  const layers = {
    stage: snapshotLayer(root),
    material: snapshotLayer(material),
    glass: snapshotLayer(glass),
    content: snapshotLayer(content),
    card: snapshotLayer(modernCard),
  };

  return {
    cardId: card.cardId,
    label: card.label ?? null,
    tone: card.tone ?? root.dataset.tone ?? null,
    materialModel: readMaterialModel(root),
    reason,
    snapshotAtIso: new Date().toISOString(),
    routePath:
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : null,
    env: {
      viewport: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : null,
      devicePixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : null,
      platformAttribute:
        typeof document !== "undefined" ? document.documentElement.getAttribute("data-capacitor-platform") : null,
      colorScheme:
        typeof window !== "undefined" && typeof window.matchMedia === "function"
          ? window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light"
          : null,
    },
    cssVars: {
      iosRefractiveCardRadius: readCssProperty(root, "--ios-refractive-card-radius"),
    },
    layers,
    diagnosis: summarizeDiagnosis(layers),
  };
}

function persistSnapshot(snapshot: ReportDebugUiCardSnapshot): void {
  const current = readPersistedSessionJson<Record<string, ReportDebugUiCardSnapshot>>(UI_CARD_DEBUG_STORAGE_KEY) ?? {};
  const entries = Object.entries({
    ...current,
    [snapshot.cardId]: snapshot,
  }).slice(-UI_CARD_DEBUG_CARD_MAX);
  writePersistedSessionJson(UI_CARD_DEBUG_STORAGE_KEY, Object.fromEntries(entries));

  const trail = readPersistedSessionJson<ReportDebugUiCardSnapshot[]>(UI_CARD_DEBUG_TRAIL_KEY) ?? [];
  writePersistedSessionJson(UI_CARD_DEBUG_TRAIL_KEY, [...trail, snapshot].slice(-UI_CARD_DEBUG_TRAIL_MAX));
}

function sampleCard(card: RegisteredUiCard, reason: ReportDebugUiCardSnapshot["reason"]): ReportDebugUiCardSnapshot {
  const snapshot = buildSnapshot(card, reason);
  persistSnapshot(snapshot);
  return snapshot;
}

function scheduleCardSample(card: RegisteredUiCard, reason: ReportDebugUiCardSnapshot["reason"]): number | null {
  if (typeof window === "undefined") return null;
  return window.setTimeout(() => {
    if (registeredCards.get(card.cardId)?.root !== card.root) return;
    sampleCard(card, reason);
  }, reason === "delayed-layout" ? 250 : 0);
}

export function registerReportDebugUiCard(input: {
  cardId: string;
  label?: string;
  tone?: string;
  root: HTMLElement | null;
}): () => void {
  if (typeof window === "undefined" || !input.root || !input.cardId) return () => undefined;

  const card: RegisteredUiCard = {
    cardId: input.cardId,
    label: input.label,
    tone: input.tone,
    root: input.root,
  };
  registeredCards.set(card.cardId, card);

  const timers: number[] = [];
  const mountTimer = scheduleCardSample(card, "mount");
  if (mountTimer !== null) timers.push(mountTimer);
  const frame = window.requestAnimationFrame?.(() => {
    if (registeredCards.get(card.cardId)?.root !== card.root) return;
    sampleCard(card, "after-layout");
  });
  const delayedTimer = scheduleCardSample(card, "delayed-layout");
  if (delayedTimer !== null) timers.push(delayedTimer);

  return () => {
    if (registeredCards.get(card.cardId)?.root === card.root) {
      registeredCards.delete(card.cardId);
    }
    for (const timer of timers) window.clearTimeout(timer);
    if (typeof frame === "number") window.cancelAnimationFrame?.(frame);
  };
}

export function collectReportDebugUiCardSnapshots(
  reason: ReportDebugUiCardSnapshot["reason"] = "report-debug"
): ReportDebugUiCardSnapshot[] {
  if (typeof window === "undefined") return [];
  return Array.from(registeredCards.values()).map((card) => sampleCard(card, reason));
}

export function readReportDebugUiCardSnapshots(): ReportDebugUiCardSnapshot[] {
  const current = readPersistedSessionJson<Record<string, ReportDebugUiCardSnapshot>>(UI_CARD_DEBUG_STORAGE_KEY);
  if (!current || typeof current !== "object") return [];
  return Object.values(current).slice(-UI_CARD_DEBUG_CARD_MAX);
}

export function readReportDebugUiCardTrail(limit = UI_CARD_DEBUG_TRAIL_MAX): ReportDebugUiCardSnapshot[] {
  if (limit <= 0) return [];
  const trail = readPersistedSessionJson<ReportDebugUiCardSnapshot[]>(UI_CARD_DEBUG_TRAIL_KEY);
  return Array.isArray(trail) ? trail.slice(Math.max(0, trail.length - limit)) : [];
}
